import { supabase } from '../lib/supabaseClient';
import type {
  Asset,
  AssetCondition,
  AssetEvent,
  MaintenanceOrder,
  MaintenanceOrderEvidence,
  MaintenanceOrderType,
  MaintenanceSchedule,
  UUID,
} from '../types/domain';

/**
 * Assets & Maintenance (FASE 11, migration 0048) — reads and writes wired to
 * the real backend. Every write below calls a permission-gated `_checked`
 * RPC — never a direct `.insert()`/`.update()`. Asset/asset_events RLS is
 * RESTRICTIVE `false` on INSERT/UPDATE/DELETE from the start (not
 * permission-check-only — the exact FASE 10 raw-bypass lesson), so a direct
 * `.insert()`/`.update()` call would fail even if attempted here.
 */

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

export async function listAssets(): Promise<Asset[]> {
  const { data, error } = await supabase
    .from('assets')
    .select(
      'asset_id, tenant_id, branch_id, asset_tag, name, category, condition, status, acquisition_date, acquisition_cost, disposed_at, disposal_requested_by, disposal_approved_by, created_at, created_by'
    )
    .order('created_at', { ascending: false });
  if (error) throw new Error(`listAssets failed: ${error.message}`);
  return (data ?? []) as Asset[];
}

export async function getAsset(assetId: UUID): Promise<Asset | null> {
  const { data, error } = await supabase
    .from('assets')
    .select(
      'asset_id, tenant_id, branch_id, asset_tag, name, category, condition, status, acquisition_date, acquisition_cost, disposed_at, disposal_requested_by, disposal_approved_by, created_at, created_by'
    )
    .eq('asset_id', assetId)
    .maybeSingle();
  if (error) throw new Error(`getAsset failed: ${error.message}`);
  return data as Asset | null;
}

export async function listAssetEvents(assetId: UUID): Promise<AssetEvent[]> {
  const { data, error } = await supabase
    .from('asset_events')
    .select('event_id, tenant_id, branch_id, asset_id, event_type, before_json, after_json, occurred_at, created_by')
    .eq('asset_id', assetId)
    .order('occurred_at', { ascending: false });
  if (error) throw new Error(`listAssetEvents failed: ${error.message}`);
  return (data ?? []) as AssetEvent[];
}

/** Calls fn_register_asset_checked. Duplicate asset_tag is rejected at the DB layer (chk/existence check inside fn_register_asset), not frontend-only. */
export async function registerAsset(
  branchId: UUID,
  assetTag: string,
  name: string,
  category: string | null,
  acquisitionDate: string | null,
  acquisitionCost: number | null
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_register_asset_checked', {
    p_branch_id: branchId,
    p_asset_tag: assetTag,
    p_name: name,
    p_category: category,
    p_acquisition_date: acquisitionDate,
    p_acquisition_cost: acquisitionCost,
  });
  if (error) throw new Error(`fn_register_asset_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_record_condition_change_checked. Blocked on a DISPOSED asset (server-enforced). */
export async function recordConditionChange(assetId: UUID, newCondition: AssetCondition): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_record_condition_change_checked', {
    p_asset_id: assetId,
    p_new_condition: newCondition,
  });
  if (error) throw new Error(`fn_record_condition_change_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_request_asset_disposal_checked (the "maker" half of disposal maker-checker). */
export async function requestAssetDisposal(assetId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_request_asset_disposal_checked', { p_asset_id: assetId });
  if (error) throw new Error(`fn_request_asset_disposal_checked failed: ${error.message}`);
}

/** Calls fn_approve_asset_disposal_checked (the "checker" half). Server rejects self-approval (approver === requester) even for a permitted user. */
export async function approveAssetDisposal(assetId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_approve_asset_disposal_checked', { p_asset_id: assetId });
  if (error) throw new Error(`fn_approve_asset_disposal_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Maintenance schedules
// ---------------------------------------------------------------------------

export async function listMaintenanceSchedules(): Promise<MaintenanceSchedule[]> {
  const { data, error } = await supabase
    .from('maintenance_schedules')
    .select('schedule_id, tenant_id, asset_id, interval_days, next_due_date, last_done_date, status, created_at')
    .order('next_due_date');
  if (error) throw new Error(`listMaintenanceSchedules failed: ${error.message}`);
  return (data ?? []) as MaintenanceSchedule[];
}

/** Calls fn_create_maintenance_schedule_checked. interval_days/next_due_date are caller-supplied real inputs — no interval is invented server- or client-side. */
export async function createMaintenanceSchedule(assetId: UUID, intervalDays: number, nextDueDate: string): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_maintenance_schedule_checked', {
    p_asset_id: assetId,
    p_interval_days: intervalDays,
    p_next_due_date: nextDueDate,
  });
  if (error) throw new Error(`fn_create_maintenance_schedule_checked failed: ${error.message}`);
  return data as UUID;
}

export async function setMaintenanceScheduleStatus(scheduleId: UUID, status: 'ACTIVE' | 'PAUSED'): Promise<void> {
  const { error } = await supabase.rpc('fn_set_maintenance_schedule_status_checked', {
    p_schedule_id: scheduleId,
    p_status: status,
  });
  if (error) throw new Error(`fn_set_maintenance_schedule_status_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Maintenance orders (work orders)
// ---------------------------------------------------------------------------

export async function listMaintenanceOrders(): Promise<MaintenanceOrder[]> {
  const { data, error } = await supabase
    .from('maintenance_orders')
    .select(
      'order_id, tenant_id, branch_id, asset_id, order_no, order_type, schedule_id, status, reported_by, assigned_to, opened_at, started_at, completed_at, cost, notes'
    )
    .order('opened_at', { ascending: false });
  if (error) throw new Error(`listMaintenanceOrders failed: ${error.message}`);
  return (data ?? []) as MaintenanceOrder[];
}

const MAINTENANCE_EVIDENCE_BUCKET = 'maintenance-evidence';

export async function listMaintenanceOrderEvidence(orderId: UUID): Promise<MaintenanceOrderEvidence[]> {
  const { data, error } = await supabase
    .from('maintenance_order_evidence')
    .select(
      'evidence_id, tenant_id, order_id, description, external_ref, storage_bucket, storage_path, original_filename, content_type, size_bytes, status, removed_at, removed_by, request_id, uploaded_at, uploaded_by'
    )
    .eq('order_id', orderId)
    .order('uploaded_at', { ascending: false });
  if (error) throw new Error(`listMaintenanceOrderEvidence failed: ${error.message}`);
  return (data ?? []) as MaintenanceOrderEvidence[];
}

/** Calls fn_open_maintenance_order_checked. scheduleId is optional (null for a BREAKDOWN report with no linked preventive schedule). Rejected server-side if the asset is DISPOSED. */
export async function openMaintenanceOrder(
  branchId: UUID,
  assetId: UUID,
  orderType: MaintenanceOrderType,
  scheduleId: UUID | null,
  notes: string | null
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_open_maintenance_order_checked', {
    p_branch_id: branchId,
    p_asset_id: assetId,
    p_order_type: orderType,
    p_schedule_id: scheduleId,
    p_notes: notes,
  });
  if (error) throw new Error(`fn_open_maintenance_order_checked failed: ${error.message}`);
  return data as UUID;
}

export async function assignMaintenanceOrder(orderId: UUID, assignedTo: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_assign_maintenance_order_checked', {
    p_order_id: orderId,
    p_assigned_to: assignedTo,
  });
  if (error) throw new Error(`fn_assign_maintenance_order_checked failed: ${error.message}`);
}

/** OPEN -> IN_PROGRESS only (server-enforced canonical transition). */
export async function startMaintenanceOrder(orderId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_start_maintenance_order_checked', { p_order_id: orderId });
  if (error) throw new Error(`fn_start_maintenance_order_checked failed: ${error.message}`);
}

/** OPEN/IN_PROGRESS -> COMPLETED (server-enforced; rejects an already-COMPLETED/CANCELLED order — idempotent-safe against retry). */
export async function completeMaintenanceOrder(orderId: UUID, cost: number): Promise<void> {
  const { error } = await supabase.rpc('fn_complete_maintenance_order_checked', {
    p_order_id: orderId,
    p_cost: cost,
  });
  if (error) throw new Error(`fn_complete_maintenance_order_checked failed: ${error.message}`);
}

/** OPEN/IN_PROGRESS -> CANCELLED; releases the asset back to ACTIVE and records the reason. */
export async function cancelMaintenanceOrder(orderId: UUID, reason: string): Promise<void> {
  const { error } = await supabase.rpc('fn_cancel_maintenance_order_checked', {
    p_order_id: orderId,
    p_reason: reason,
  });
  if (error) throw new Error(`fn_cancel_maintenance_order_checked failed: ${error.message}`);
}

/**
 * Real file upload (migration 0049): uploads directly to the private
 * "maintenance-evidence" Storage bucket under RLS (tenant-prefixed path +
 * a live work-order-existence check + maintenance_order.update permission
 * — this is the authoritative authorization gate for the binary upload;
 * there is no SECURITY DEFINER wrapper for raw file bytes in Supabase's
 * architecture), then records the metadata row via the permission-gated
 * RPC. Path convention is fixed here (not caller-configurable) to always
 * match what both the storage RLS policy and the RPC's own server-side
 * prefix check require: "{tenant_id}/{order_id}/{evidence-uuid}-{filename}".
 */
export async function attachMaintenanceEvidence(
  tenantId: UUID,
  orderId: UUID,
  file: File,
  description: string | null
): Promise<UUID> {
  const storagePath = `${tenantId}/${orderId}/${crypto.randomUUID()}-${file.name}`;
  const { error: uploadError } = await supabase.storage.from(MAINTENANCE_EVIDENCE_BUCKET).upload(storagePath, file, {
    contentType: file.type || 'application/octet-stream',
    upsert: false,
  });
  if (uploadError) throw new Error(`evidence upload failed: ${uploadError.message}`);

  const { data, error } = await supabase.rpc('fn_attach_maintenance_evidence_checked', {
    p_order_id: orderId,
    p_storage_path: storagePath,
    p_original_filename: file.name,
    p_content_type: file.type || 'application/octet-stream',
    p_size_bytes: file.size,
    p_description: description,
  });
  if (error) throw new Error(`fn_attach_maintenance_evidence_checked failed: ${error.message}`);
  return data as UUID;
}

/** Short-lived signed URL (default 5 minutes) for viewing/downloading one evidence object — never a permanent public URL, since the bucket is private and gated by storage RLS. */
export async function getMaintenanceEvidenceUrl(storagePath: string, expiresInSeconds = 300): Promise<string> {
  const { data, error } = await supabase.storage.from(MAINTENANCE_EVIDENCE_BUCKET).createSignedUrl(storagePath, expiresInSeconds);
  if (error) throw new Error(`getMaintenanceEvidenceUrl failed: ${error.message}`);
  return data.signedUrl;
}

/** Logical removal only (status -> REMOVED) — the underlying storage object and this row are never deleted, so work-order history stays reproducible. */
export async function removeMaintenanceEvidence(evidenceId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_remove_maintenance_evidence_checked', { p_evidence_id: evidenceId });
  if (error) throw new Error(`fn_remove_maintenance_evidence_checked failed: ${error.message}`);
}
